import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';

import type { CourierMondayStatus } from 'features/courier-portal/services/courierPortalService';
import RecruitmentStepper from './RecruitmentStepper';
import RecruitmentRejectionCard from './RecruitmentRejectionCard';

interface Props {
  status: CourierMondayStatus | null;
  loading: boolean;
  errored: boolean;
  onRefresh: () => void;
}

const RecruitmentStageSection: React.FC<Props> = ({
  status,
  loading,
  errored,
  onRefresh,
}) => {
  const { t } = useTranslation();

  return (
    <section className="mb-4 rounded-xl border border-gray-200 bg-white p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="text-base sm:text-lg font-bold text-gray-900">
            {t('portal.stage.title')}
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
            {t('portal.stage.subtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-semibold text-[#bf0035] transition-colors hover:bg-[#bf0035]/[0.05] disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="animate-spin" size={14} />
          ) : (
            <RefreshCw size={14} />
          )}
          {t('portal.stage.refresh')}
        </button>
      </div>

      {loading && !status ? (
        <div className="flex items-center justify-center py-8 text-gray-500">
          <Loader2 className="animate-spin" size={20} />
        </div>
      ) : errored && !status ? (
        <div className="flex items-center gap-2 border-s-2 border-[#bf0035] bg-[#bf0035]/[0.04] px-4 py-3 text-sm text-[#bf0035]">
          <AlertTriangle size={16} />
          {t('portal.stage.fetchFailed')}
        </div>
      ) : status ? (
        <>
          {status.stage === 'DISQUALIFIED' ? (
            <RecruitmentRejectionCard failReason={status.failReason} />
          ) : (
            <>
              {status.isFailure ? (
                <div className="mb-4 flex items-start gap-2 border-s-2 border-[#bf0035] bg-[#bf0035]/[0.04] px-4 py-3">
                  <AlertTriangle
                    size={18}
                    className="mt-0.5 shrink-0 text-[#bf0035]"
                  />
                  <p className="text-sm text-gray-700">
                    {status.stage === 'STUCK'
                      ? t('portal.stage.stuckHint')
                      : t('portal.stage.incompleteHint')}
                  </p>
                </div>
              ) : null}
              <RecruitmentStepper stageOrder={status.stageOrder} />
            </>
          )}
        </>
      ) : null}
    </section>
  );
};

export default RecruitmentStageSection;
